import { supabase } from './supabase';
import { logger } from './logger';

export type InteractionType = 'like' | 'view' | 'swipe' | 'favorite';

// ─── Helpers ────────────────────────────────────────────────

function spreadTimestamp(spreadDays: number): string {
  return new Date(Date.now() - Math.random() * spreadDays * 86_400_000).toISOString();
}

function dedup<T>(arr: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set<string>();
  return arr.filter((item) => {
    const k = keyFn(item);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

async function logAudit(action: string, targetType: string, targetId: string, details?: any) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;
  await supabase.from('growth_audit_log').insert({
    admin_id: session.user.id,
    action,
    target_type: targetType,
    target_id: targetId,
    details: details || {},
  });
}

async function getSeedCandidates(campaignTag: string): Promise<string[]> {
  const { data } = await supabase
    .from('profiles')
    .select('id')
    .eq('seed_tag', campaignTag)
    .eq('role', 'candidate');
  if (!data?.length) {
    logger.error('getSeedCandidates', 'engagementService', { campaignTag });
    throw new Error('Aucun compte seed candidat dans cette campagne');
  }
  return data.map((p: any) => p.id);
}

async function getSeedEmployers(campaignTag: string): Promise<string[]> {
  const { data } = await supabase
    .from('profiles')
    .select('id')
    .eq('seed_tag', campaignTag)
    .eq('role', 'employer');
  if (!data?.length) {
    logger.error('getSeedEmployers', 'engagementService', { campaignTag });
    throw new Error('Aucun compte seed recruteur dans cette campagne');
  }
  return data.map((p: any) => p.id);
}

// ─── Video Interactions (with temporal spread) ──────────────

export async function generateVideoInteractions(params: {
  campaignTag: string;
  interaction: 'like' | 'view';
  videoIds: string[];
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('generateVideoInteractions', 'engagementService', { campaignTag: params.campaignTag, interaction: params.interaction, count: params.count });
  const seedProfiles = await getSeedCandidates(params.campaignTag);
  const days = params.spreadDays ?? 7;

  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    rows.push({
      user_id: seedProfiles[i % seedProfiles.length],
      video_id: params.videoIds[i % params.videoIds.length],
      video_source: 'candidate',
      interaction: params.interaction,
      seed_tag: params.campaignTag,
      created_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.user_id}-${r.video_id}-${r.interaction}`);
  if (uniqueRows.length === 0) return 0;

  const { error } = await supabase.from('video_interactions').upsert(uniqueRows, {
    onConflict: 'user_id,video_id,interaction',
    ignoreDuplicates: true,
  });
  if (error) {
    logger.error('generateVideoInteractions', 'engagementService', { error });
    throw error;
  }
  await logAudit('generate_video_interactions', 'interactions', params.campaignTag, {
    type: params.interaction,
    count: uniqueRows.length,
  });
  logger.technical('generateVideoInteractions', 'engagementService', { insertedCount: uniqueRows.length });
  return uniqueRows.length;
}

// ─── Swipes (with temporal spread) ──────────────────────────

export async function generateSwipes(params: {
  campaignTag: string;
  targetOfferIds: string[];
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('generateSwipes', 'engagementService', { campaignTag: params.campaignTag, count: params.count });
  const seedProfiles = await getSeedCandidates(params.campaignTag);
  const days = params.spreadDays ?? 7;

  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    rows.push({
      user_id: seedProfiles[i % seedProfiles.length],
      target_type: 'job_offer',
      target_id: params.targetOfferIds[i % params.targetOfferIds.length],
      action: 'like',
      seed_tag: params.campaignTag,
      created_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.user_id}-${r.target_type}-${r.target_id}`);
  if (uniqueRows.length === 0) return 0;

  const { error } = await supabase.from('swipe_actions').upsert(uniqueRows, {
    onConflict: 'user_id,target_type,target_id',
    ignoreDuplicates: true,
  });
  if (error) {
    logger.error('generateSwipes', 'engagementService', { error });
    throw error;
  }
  await logAudit('generate_swipes', 'interactions', params.campaignTag, { count: uniqueRows.length });
  logger.technical('generateSwipes', 'engagementService', { insertedCount: uniqueRows.length });
  return uniqueRows.length;
}

// ─── Favorites (with temporal spread) ───────────────────────

export async function generateFavorites(params: {
  campaignTag: string;
  targetType: 'offer' | 'candidate';
  targetIds: string[];
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('generateFavorites', 'engagementService', { campaignTag: params.campaignTag, targetType: params.targetType, count: params.count });
  const seedRole = params.targetType === 'offer' ? 'candidate' : 'employer';
  const seedProfiles = seedRole === 'candidate'
    ? await getSeedCandidates(params.campaignTag)
    : await getSeedEmployers(params.campaignTag);
  const days = params.spreadDays ?? 7;

  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    rows.push({
      user_id: seedProfiles[i % seedProfiles.length],
      target_type: params.targetType,
      target_id: params.targetIds[i % params.targetIds.length],
      seed_tag: params.campaignTag,
      created_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.user_id}-${r.target_type}-${r.target_id}`);
  if (uniqueRows.length === 0) return 0;

  const { error } = await supabase.from('favorites').upsert(uniqueRows, {
    onConflict: 'user_id,target_type,target_id',
    ignoreDuplicates: true,
  });
  if (error) {
    logger.error('generateFavorites', 'engagementService', { error });
    throw error;
  }
  await logAudit('generate_favorites', 'interactions', params.campaignTag, { count: uniqueRows.length });
  logger.technical('generateFavorites', 'engagementService', { insertedCount: uniqueRows.length });
  return uniqueRows.length;
}

// ─── Seed Applications (P1) ─────────────────────────────────

export async function generateSeedApplications(params: {
  campaignTag: string;
  targetOfferIds: string[];
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('generateSeedApplications', 'engagementService', { campaignTag: params.campaignTag, count: params.count });
  const seedProfiles = await getSeedCandidates(params.campaignTag);
  const days = params.spreadDays ?? 7;

  const { data: cpData } = await supabase
    .from('candidate_profiles')
    .select('id, user_id')
    .in('user_id', seedProfiles);

  const cpMap: Record<string, string> = {};
  (cpData || []).forEach((c: any) => { cpMap[c.user_id] = c.id; });

  // Create missing candidate_profiles for seed candidates that don't have one
  const missingUserIds = seedProfiles.filter((uid) => !cpMap[uid]);
  if (missingUserIds.length > 0) {
    logger.warn('generateSeedApplications_missing_cp', 'engagementService', {
      missingCount: missingUserIds.length,
      totalSeeds: seedProfiles.length,
    });
    const insertRows = missingUserIds.map((uid) => ({
      user_id: uid,
      first_name: 'Seed',
      last_name: 'Candidat',
      city: 'Paris',
    }));
    const { data: newCps, error: cpErr } = await supabase
      .from('candidate_profiles')
      .upsert(insertRows, { onConflict: 'user_id', ignoreDuplicates: true })
      .select('id, user_id');
    if (cpErr) {
      logger.error('generateSeedApplications_create_cp', 'engagementService', { error: cpErr });
    }
    (newCps || []).forEach((c: any) => { cpMap[c.user_id] = c.id; });
  }

  if (Object.keys(cpMap).length === 0) {
    throw new Error(`Aucun profil candidat trouv\u00e9 pour la campagne "${params.campaignTag}". V\u00e9rifiez que des comptes seed candidats existent.`);
  }

  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    const userId = seedProfiles[i % seedProfiles.length];
    const cpId = cpMap[userId];
    if (!cpId) continue;
    rows.push({
      candidate_id: cpId,
      job_offer_id: params.targetOfferIds[i % params.targetOfferIds.length],
      status: 'viewed',
      seed_tag: params.campaignTag,
      applied_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.candidate_id}-${r.job_offer_id}`);
  if (uniqueRows.length === 0) {
    throw new Error('Toutes les combinaisons candidat/offre existent d\u00e9j\u00e0');
  }

  const { error } = await supabase.from('applications').insert(uniqueRows);
  if (error) {
    logger.error('generateSeedApplications', 'engagementService', { error });
    throw error;
  }
  await logAudit('generate_seed_applications', 'applications', params.campaignTag, {
    count: uniqueRows.length,
  });
  logger.technical('generateSeedApplications', 'engagementService', { insertedCount: uniqueRows.length });
  return uniqueRows.length;
}

// ─── Boost Real User (P1) ───────────────────────────────────

export async function boostRealUser(params: {
  campaignTag: string;
  targetUserId: string;
  interaction: 'like' | 'view';
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('boostRealUser', 'engagementService', { targetUserId: params.targetUserId, interaction: params.interaction, count: params.count });
  const seedProfiles = await getSeedCandidates(params.campaignTag);
  const days = params.spreadDays ?? 3;

  const { data: cpData } = await supabase
    .from('candidate_profiles')
    .select('id')
    .eq('user_id', params.targetUserId)
    .maybeSingle();

  if (!cpData) {
    logger.error('boostRealUser', 'engagementService', { targetUserId: params.targetUserId, reason: 'no candidate profile' });
    throw new Error('Utilisateur cible n\'a pas de profil candidat');
  }

  const { data: videos } = await supabase
    .from('candidate_videos')
    .select('id')
    .eq('candidate_id', cpData.id)
    .eq('status', 'approved');

  if (!videos?.length) {
    logger.error('boostRealUser', 'engagementService', { targetUserId: params.targetUserId, reason: 'no approved videos' });
    throw new Error('Aucune vidéo approuvée pour cet utilisateur');
  }

  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    rows.push({
      user_id: seedProfiles[i % seedProfiles.length],
      video_id: videos[i % videos.length].id,
      video_source: 'candidate',
      interaction: params.interaction,
      seed_tag: params.campaignTag,
      created_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.user_id}-${r.video_id}-${r.interaction}`);
  if (uniqueRows.length === 0) return 0;

  const { error } = await supabase.from('video_interactions').upsert(uniqueRows, {
    onConflict: 'user_id,video_id,interaction',
    ignoreDuplicates: true,
  });
  if (error) {
    logger.error('boostRealUser', 'engagementService', { error });
    throw error;
  }
  await logAudit('boost_real_user', 'account', params.targetUserId, {
    type: params.interaction,
    count: uniqueRows.length,
    campaign: params.campaignTag,
  });
  logger.technical('boostRealUser', 'engagementService', { insertedCount: uniqueRows.length });
  return uniqueRows.length;
}

export async function boostRealOffer(params: {
  campaignTag: string;
  targetOfferId: string;
  action: 'swipe' | 'favorite';
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('boostRealOffer', 'engagementService', { targetOfferId: params.targetOfferId, action: params.action, count: params.count });
  const seedProfiles = await getSeedCandidates(params.campaignTag);
  const days = params.spreadDays ?? 3;

  if (params.action === 'swipe') {
    const rows: any[] = [];
    for (let i = 0; i < params.count; i++) {
      rows.push({
        user_id: seedProfiles[i % seedProfiles.length],
        target_type: 'job_offer',
        target_id: params.targetOfferId,
        action: 'like',
        seed_tag: params.campaignTag,
        created_at: spreadTimestamp(days),
      });
    }
    const uniqueRows = dedup(rows, (r) => `${r.user_id}-${r.target_id}`);
    if (uniqueRows.length === 0) return 0;
    const { error } = await supabase.from('swipe_actions').upsert(uniqueRows, {
      onConflict: 'user_id,target_type,target_id',
      ignoreDuplicates: true,
    });
    if (error) {
      logger.error('boostRealOffer', 'engagementService', { error, action: 'swipe' });
      throw error;
    }
    await logAudit('boost_real_offer', 'offer', params.targetOfferId, { action: 'swipe', count: uniqueRows.length });
    logger.technical('boostRealOffer', 'engagementService', { action: 'swipe', insertedCount: uniqueRows.length });
    return uniqueRows.length;
  } else {
    const rows: any[] = [];
    for (let i = 0; i < params.count; i++) {
      rows.push({
        user_id: seedProfiles[i % seedProfiles.length],
        target_type: 'offer',
        target_id: params.targetOfferId,
        seed_tag: params.campaignTag,
        created_at: spreadTimestamp(days),
      });
    }
    const uniqueRows = dedup(rows, (r) => `${r.user_id}-${r.target_id}`);
    if (uniqueRows.length === 0) return 0;
    const { error } = await supabase.from('favorites').upsert(uniqueRows, {
      onConflict: 'user_id,target_type,target_id',
      ignoreDuplicates: true,
    });
    if (error) {
      logger.error('boostRealOffer', 'engagementService', { error, action: 'favorite' });
      throw error;
    }
    await logAudit('boost_real_offer', 'offer', params.targetOfferId, { action: 'favorite', count: uniqueRows.length });
    logger.technical('boostRealOffer', 'engagementService', { action: 'favorite', insertedCount: uniqueRows.length });
    return uniqueRows.length;
  }
}

// ─── Employer Engagement (P3) ───────────────────────────────

export async function generateEmployerProfileViews(params: {
  campaignTag: string;
  targetCandidateIds: string[];
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('generateEmployerProfileViews', 'engagementService', { campaignTag: params.campaignTag, count: params.count });
  const seedEmployers = await getSeedEmployers(params.campaignTag);
  const days = params.spreadDays ?? 7;

  // Get employer_profiles.id for seed employers
  const { data: epData } = await supabase
    .from('employer_profiles')
    .select('id, user_id')
    .in('user_id', seedEmployers);
  const epIds = (epData || []).map((e: any) => e.id);
  if (epIds.length === 0) {
    logger.error('generateEmployerProfileViews', 'engagementService', { reason: 'no seed employer profiles' });
    throw new Error('Aucun profil employeur seed trouvé');
  }

  // targetCandidateIds are candidate_profiles.id (not user_id)
  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    rows.push({
      employer_id: epIds[i % epIds.length],
      candidate_id: params.targetCandidateIds[i % params.targetCandidateIds.length],
      seed_tag: params.campaignTag,
      viewed_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.employer_id}-${r.candidate_id}-${r.viewed_at.slice(0, 13)}`);
  if (uniqueRows.length === 0) return 0;

  const { error } = await supabase.from('profile_views').insert(uniqueRows);
  if (error) {
    logger.error('generateEmployerProfileViews', 'engagementService', { error });
    throw error;
  }
  await logAudit('generate_employer_profile_views', 'interactions', params.campaignTag, {
    count: uniqueRows.length,
  });
  logger.technical('generateEmployerProfileViews', 'engagementService', { insertedCount: uniqueRows.length });
  return uniqueRows.length;
}

export async function generateEmployerSelections(params: {
  campaignTag: string;
  targetCandidateIds: string[];
  count: number;
  spreadDays?: number;
}): Promise<number> {
  logger.functional('generateEmployerSelections', 'engagementService', { campaignTag: params.campaignTag, count: params.count });
  const seedEmployers = await getSeedEmployers(params.campaignTag);
  const days = params.spreadDays ?? 7;

  // Get employer_profiles.id for seed employers
  const { data: epData } = await supabase
    .from('employer_profiles')
    .select('id, user_id')
    .in('user_id', seedEmployers);
  const epIds = (epData || []).map((e: any) => e.id);
  if (epIds.length === 0) {
    logger.error('generateEmployerSelections', 'engagementService', { reason: 'no seed employer profiles' });
    throw new Error('Aucun profil employeur seed trouvé');
  }

  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    rows.push({
      employer_id: epIds[i % epIds.length],
      candidate_id: params.targetCandidateIds[i % params.targetCandidateIds.length],
      seed_tag: params.campaignTag,
      selected_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.employer_id}-${r.candidate_id}`);
  if (uniqueRows.length === 0) return 0;

  const { error } = await supabase.from('employer_selections').insert(uniqueRows);
  if (error) {
    logger.error('generateEmployerSelections', 'engagementService', { error });
    throw error;
  }
  await logAudit('generate_employer_selections', 'interactions', params.campaignTag, {
    count: uniqueRows.length,
  });
  logger.technical('generateEmployerSelections', 'engagementService', { insertedCount: uniqueRows.length });
  return uniqueRows.length;
}

// ─── Fetch Real Users / Offers (for picker) ─────────────────

export async function fetchRealUsers(search: string): Promise<{ userId: string; name: string; type: string; city: string }[]> {
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, role')
    .is('seed_tag', null)
    .neq('role', 'admin')
    .limit(200);

  if (!profiles?.length) return [];

  const userIds = profiles.map((p: any) => p.id);
  const roleMap: Record<string, string> = {};
  profiles.forEach((p: any) => { roleMap[p.id] = p.role; });

  const [{ data: candidates }, { data: employers }] = await Promise.all([
    supabase.from('candidate_profiles').select('user_id, first_name, last_name, city').in('user_id', userIds),
    supabase.from('employer_profiles').select('user_id, first_name, last_name, company_city, company_name').in('user_id', userIds),
  ]);

  const results: { userId: string; name: string; type: string; city: string }[] = [];
  (candidates || []).forEach((c: any) => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    results.push({ userId: c.user_id, name, type: 'candidate', city: c.city || '' });
  });
  (employers || []).forEach((e: any) => {
    const name = e.company_name || `${e.first_name || ''} ${e.last_name || ''}`.trim();
    results.push({ userId: e.user_id, name, type: 'employer', city: e.company_city || '' });
  });

  logger.technical('fetchRealUsers', 'engagementService', { totalResults: results.length });
  if (!search) return results.slice(0, 50);
  const q = search.toLowerCase();
  return results
    .filter((r) => r.name.toLowerCase().includes(q) || r.city.toLowerCase().includes(q))
    .slice(0, 50);
}

export async function fetchRealOffers(search: string): Promise<{ id: string; title: string; city: string; employer: string }[]> {
  const { data, error } = await supabase
    .from('job_offers')
    .select('id, position_name, establishment_city, employer_profiles!job_offers_employer_id_fkey(company_name)')
    .is('seed_tag', null)
    .eq('status', 'active')
    .limit(200);

  if (error) {
    logger.error('fetchRealOffers', 'engagementService', { error });
    throw error;
  }

  const results = (data || []).map((o: any) => ({
    id: o.id,
    title: o.position_name || 'Sans titre',
    city: o.establishment_city || '',
    employer: o.employer_profiles?.company_name || '',
  }));

  logger.technical('fetchRealOffers', 'engagementService', { totalResults: results.length });
  if (!search) return results.slice(0, 50);
  const q = search.toLowerCase();
  return results
    .filter((r) => r.title.toLowerCase().includes(q) || r.city.toLowerCase().includes(q) || r.employer.toLowerCase().includes(q))
    .slice(0, 50);
}

export async function fetchAvailableVideos(): Promise<{ id: string; candidateName: string }[]> {
  const { data, error } = await supabase
    .from('candidate_videos')
    .select('id, candidate_id')
    .eq('status', 'approved')
    .limit(200);

  if (error) {
    logger.error('fetchAvailableVideos', 'engagementService', { error });
    throw error;
  }
  const results = (data || []).map((v: any) => ({
    id: v.id,
    candidateName: v.candidate_id,
  }));
  logger.technical('fetchAvailableVideos', 'engagementService', { count: results.length });
  return results;
}

export async function fetchAvailableOffers(): Promise<{ id: string; title: string }[]> {
  const { data, error } = await supabase
    .from('job_offers')
    .select('id, position_name')
    .eq('status', 'active')
    .limit(200);

  if (error) {
    logger.error('fetchAvailableOffers', 'engagementService', { error });
    throw error;
  }
  const results = (data || []).map((o: any) => ({
    id: o.id,
    title: o.position_name || 'Sans titre',
  }));
  logger.technical('fetchAvailableOffers', 'engagementService', { count: results.length });
  return results;
}

// ─── Profile-Centric Types ─────────────────────────────────

export interface SearchResult {
  id: string;
  userId?: string;
  name: string;
  subtitle: string;
  type: 'candidate' | 'employer' | 'offer';
  isSeed: boolean;
  metric?: number;
}

export type CandidateSortBy = 'likes' | 'views' | 'videos' | 'applications' | 'profile_views' | 'least_active';
export type EmployerSortBy = 'offers' | 'applications_received' | 'selections' | 'profile_views_made';
export type OfferSortBy = 'swipes' | 'favorites' | 'applications' | 'least_active';

export interface VideoDetail {
  id: string;
  status: string;
  duration: number;
  createdAt: string;
  likes: number;
  views: number;
}

export interface CandidateProfileDetail {
  userId: string;
  candidateProfileId: string;
  firstName: string;
  lastName: string;
  city: string;
  email: string;
  isSeed: boolean;
  seedTag: string | null;
  photoUrl: string | null;
  videos: VideoDetail[];
  stats: {
    totalLikes: number;
    totalViews: number;
    profileViewsReceived: number;
    applicationsCount: number;
    favoritesReceived: number;
  };
  applications: { id: string; offerId: string; offerTitle: string; status: string; date: string }[];
}

export interface OfferStats {
  id: string;
  positionName: string;
  city: string;
  contractType: string;
  swipes: number;
  favorites: number;
  applications: number;
}

export interface EmployerProfileDetail {
  userId: string;
  employerProfileId: string;
  firstName: string;
  lastName: string;
  companyName: string;
  city: string;
  email: string;
  isSeed: boolean;
  seedTag: string | null;
  offers: OfferStats[];
  stats: {
    totalOffers: number;
    totalApplicationsReceived: number;
    profileViewsMade: number;
    selectionsMade: number;
  };
}

export interface OfferDetailData {
  id: string;
  positionName: string;
  city: string;
  contractType: string;
  salaryMin: number | null;
  salaryMax: number | null;
  description: string;
  employerName: string;
  employerUserId: string;
  isSeed: boolean;
  seedTag: string | null;
  stats: { swipes: number; favorites: number; applications: number };
  applications: { id: string; candidateProfileId: string; candidateName: string; status: string; date: string }[];
}

// ─── Helpers (profile-centric) ──────────────────────────────

function countBy<T>(arr: T[], keyFn: (item: T) => string): Record<string, number> {
  const m: Record<string, number> = {};
  arr.forEach((item) => { const k = keyFn(item); m[k] = (m[k] || 0) + 1; });
  return m;
}

// ─── Unified Search ─────────────────────────────────────────

export async function searchAll(query: string, tab: 'candidate' | 'employer' | 'offer'): Promise<SearchResult[]> {
  const q = query.toLowerCase().trim();
  if (!q) return [];

  if (tab === 'candidate') {
    const { data: candidates } = await supabase
      .from('candidate_profiles')
      .select('id, user_id, first_name, last_name, city')
      .limit(500);
    if (!candidates?.length) return [];

    const userIds = candidates.map((c: any) => c.user_id);
    const { data: profiles } = await supabase.from('profiles').select('id, seed_tag, email').in('id', userIds);
    const pMap = new Map((profiles || []).map((p: any) => [p.id, p]));

    return candidates
      .filter((c: any) => {
        const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
        const p = pMap.get(c.user_id);
        return name.includes(q) || (p?.email || '').toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q);
      })
      .slice(0, 30)
      .map((c: any) => {
        const p = pMap.get(c.user_id);
        return {
          id: c.user_id,
          userId: c.user_id,
          name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Sans nom',
          subtitle: [c.city, p?.email].filter(Boolean).join(' \u00b7 '),
          type: 'candidate' as const,
          isSeed: !!p?.seed_tag,
        };
      });
  }

  if (tab === 'employer') {
    const { data: employers } = await supabase
      .from('employer_profiles')
      .select('id, user_id, first_name, last_name, company_name, company_city')
      .limit(500);
    if (!employers?.length) return [];

    const userIds = employers.map((e: any) => e.user_id);
    const { data: profiles } = await supabase.from('profiles').select('id, seed_tag, email').in('id', userIds);
    const pMap = new Map((profiles || []).map((p: any) => [p.id, p]));

    return employers
      .filter((e: any) => {
        const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
        const cn = (e.company_name || '').toLowerCase();
        const p = pMap.get(e.user_id);
        return name.includes(q) || cn.includes(q) || (p?.email || '').toLowerCase().includes(q) || (e.company_city || '').toLowerCase().includes(q);
      })
      .slice(0, 30)
      .map((e: any) => {
        const p = pMap.get(e.user_id);
        return {
          id: e.user_id,
          userId: e.user_id,
          name: e.company_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Sans nom',
          subtitle: [e.company_city, p?.email].filter(Boolean).join(' \u00b7 '),
          type: 'employer' as const,
          isSeed: !!p?.seed_tag,
        };
      });
  }

  if (tab === 'offer') {
    const { data: offers } = await supabase
      .from('job_offers')
      .select('id, position_name, establishment_city, contract_type, seed_tag, employer_id')
      .limit(500);
    if (!offers?.length) return [];

    const epIds = [...new Set((offers || []).map((o: any) => o.employer_id).filter(Boolean))];
    const epMap = new Map<string, string>();
    if (epIds.length > 0) {
      const { data: eps } = await supabase.from('employer_profiles').select('id, company_name').in('id', epIds);
      (eps || []).forEach((e: any) => epMap.set(e.id, e.company_name || ''));
    }

    return offers
      .filter((o: any) =>
        (o.position_name || '').toLowerCase().includes(q) ||
        (o.establishment_city || '').toLowerCase().includes(q) ||
        (epMap.get(o.employer_id) || '').toLowerCase().includes(q)
      )
      .slice(0, 30)
      .map((o: any) => ({
        id: o.id,
        name: o.position_name || 'Sans titre',
        subtitle: [o.establishment_city, o.contract_type, epMap.get(o.employer_id)].filter(Boolean).join(' \u00b7 '),
        type: 'offer' as const,
        isSeed: !!o.seed_tag,
      }));
  }

  return [];
}

// ─── Ranking: Candidates ────────────────────────────────────

export async function fetchRankedCandidates(sortBy: CandidateSortBy, limit = 20): Promise<SearchResult[]> {
  const { data: candidates } = await supabase
    .from('candidate_profiles')
    .select('id, user_id, first_name, last_name, city')
    .limit(500);
  if (!candidates?.length) return [];

  const cpIds = candidates.map((c: any) => c.id);
  const userIds = candidates.map((c: any) => c.user_id);
  const { data: profiles } = await supabase.from('profiles').select('id, seed_tag, email').in('id', userIds);
  const pMap = new Map((profiles || []).map((p: any) => [p.id, p]));

  let countMap: Record<string, number> = {};

  if (sortBy === 'likes' || sortBy === 'views' || sortBy === 'least_active') {
    const { data: videos } = await supabase.from('candidate_videos').select('id, candidate_id').in('candidate_id', cpIds).limit(5000);
    const videoIds = (videos || []).map((v: any) => v.id);
    const videoCpMap: Record<string, string> = {};
    (videos || []).forEach((v: any) => { videoCpMap[v.id] = v.candidate_id; });

    if (videoIds.length > 0) {
      let intQuery = supabase.from('video_interactions').select('video_id').in('video_id', videoIds).limit(5000);
      if (sortBy !== 'least_active') {
        intQuery = intQuery.eq('interaction', sortBy === 'likes' ? 'like' : 'view');
      }
      const { data: interactions } = await intQuery;
      (interactions || []).forEach((i: any) => {
        const cpId = videoCpMap[i.video_id];
        if (cpId) countMap[cpId] = (countMap[cpId] || 0) + 1;
      });
    }
  } else if (sortBy === 'videos') {
    const { data: videos } = await supabase.from('candidate_videos').select('candidate_id').in('candidate_id', cpIds).limit(5000);
    countMap = countBy(videos || [], (v: any) => v.candidate_id);
  } else if (sortBy === 'applications') {
    const { data: apps } = await supabase.from('applications').select('candidate_id').in('candidate_id', cpIds).limit(5000);
    countMap = countBy(apps || [], (a: any) => a.candidate_id);
  } else if (sortBy === 'profile_views') {
    const { data: views } = await supabase.from('profile_views').select('candidate_id').in('candidate_id', cpIds).limit(5000);
    countMap = countBy(views || [], (v: any) => v.candidate_id);
  }

  const asc = sortBy === 'least_active';
  return candidates
    .map((c: any) => ({ ...c, metric: countMap[c.id] || 0 }))
    .sort((a: any, b: any) => asc ? a.metric - b.metric : b.metric - a.metric)
    .slice(0, limit)
    .map((c: any) => {
      const p = pMap.get(c.user_id);
      return {
        id: c.user_id,
        userId: c.user_id,
        name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Sans nom',
        subtitle: [c.city, p?.email].filter(Boolean).join(' \u00b7 '),
        type: 'candidate' as const,
        isSeed: !!p?.seed_tag,
        metric: c.metric,
      };
    });
}

// ─── Ranking: Employers ─────────────────────────────────────

export async function fetchRankedEmployers(sortBy: EmployerSortBy, limit = 20): Promise<SearchResult[]> {
  const { data: employers } = await supabase
    .from('employer_profiles')
    .select('id, user_id, first_name, last_name, company_name, company_city')
    .limit(500);
  if (!employers?.length) return [];

  const epIds = employers.map((e: any) => e.id);
  const userIds = employers.map((e: any) => e.user_id);
  const { data: profiles } = await supabase.from('profiles').select('id, seed_tag, email').in('id', userIds);
  const pMap = new Map((profiles || []).map((p: any) => [p.id, p]));

  let countMap: Record<string, number> = {};

  if (sortBy === 'offers') {
    const { data: offers } = await supabase.from('job_offers').select('employer_id').in('employer_id', epIds).limit(5000);
    countMap = countBy(offers || [], (o: any) => o.employer_id);
  } else if (sortBy === 'applications_received') {
    const { data: offers } = await supabase.from('job_offers').select('id, employer_id').in('employer_id', epIds).limit(5000);
    const offerIds = (offers || []).map((o: any) => o.id);
    const offerEpMap: Record<string, string> = {};
    (offers || []).forEach((o: any) => { offerEpMap[o.id] = o.employer_id; });

    if (offerIds.length > 0) {
      const { data: apps } = await supabase.from('applications').select('job_offer_id').in('job_offer_id', offerIds).limit(5000);
      (apps || []).forEach((a: any) => {
        const epId = offerEpMap[a.job_offer_id];
        if (epId) countMap[epId] = (countMap[epId] || 0) + 1;
      });
    }
  } else if (sortBy === 'selections') {
    const { data: sels } = await supabase.from('employer_selections').select('employer_id').in('employer_id', epIds).limit(5000);
    countMap = countBy(sels || [], (s: any) => s.employer_id);
  } else if (sortBy === 'profile_views_made') {
    const { data: views } = await supabase.from('profile_views').select('employer_id').in('employer_id', epIds).limit(5000);
    countMap = countBy(views || [], (v: any) => v.employer_id);
  }

  return employers
    .map((e: any) => ({ ...e, metric: countMap[e.id] || 0 }))
    .sort((a: any, b: any) => b.metric - a.metric)
    .slice(0, limit)
    .map((e: any) => {
      const p = pMap.get(e.user_id);
      return {
        id: e.user_id,
        userId: e.user_id,
        name: e.company_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Sans nom',
        subtitle: [e.company_city, p?.email].filter(Boolean).join(' \u00b7 '),
        type: 'employer' as const,
        isSeed: !!p?.seed_tag,
        metric: e.metric,
      };
    });
}

// ─── Ranking: Offers ────────────────────────────────────────

export async function fetchRankedOffers(sortBy: OfferSortBy, limit = 20): Promise<SearchResult[]> {
  const { data: offers } = await supabase
    .from('job_offers')
    .select('id, position_name, establishment_city, contract_type, seed_tag, employer_id')
    .limit(500);
  if (!offers?.length) return [];

  const offerIds = offers.map((o: any) => o.id);
  let countMap: Record<string, number> = {};

  if (sortBy === 'swipes' || sortBy === 'least_active') {
    const { data: swipes } = await supabase.from('swipe_actions').select('target_id').eq('target_type', 'job_offer').in('target_id', offerIds).limit(5000);
    countMap = countBy(swipes || [], (s: any) => s.target_id);
  } else if (sortBy === 'favorites') {
    const { data: favs } = await supabase.from('favorites').select('target_id').eq('target_type', 'offer').in('target_id', offerIds).limit(5000);
    countMap = countBy(favs || [], (f: any) => f.target_id);
  } else if (sortBy === 'applications') {
    const { data: apps } = await supabase.from('applications').select('job_offer_id').in('job_offer_id', offerIds).limit(5000);
    countMap = countBy(apps || [], (a: any) => a.job_offer_id);
  }

  const epIds = [...new Set(offers.map((o: any) => o.employer_id).filter(Boolean))];
  const epMap = new Map<string, string>();
  if (epIds.length > 0) {
    const { data: eps } = await supabase.from('employer_profiles').select('id, company_name').in('id', epIds);
    (eps || []).forEach((e: any) => epMap.set(e.id, e.company_name || ''));
  }

  const asc = sortBy === 'least_active';
  return offers
    .map((o: any) => ({ ...o, metric: countMap[o.id] || 0 }))
    .sort((a: any, b: any) => asc ? a.metric - b.metric : b.metric - a.metric)
    .slice(0, limit)
    .map((o: any) => ({
      id: o.id,
      name: o.position_name || 'Sans titre',
      subtitle: [o.establishment_city, o.contract_type, epMap.get(o.employer_id)].filter(Boolean).join(' \u00b7 '),
      type: 'offer' as const,
      isSeed: !!o.seed_tag,
      metric: o.metric,
    }));
}

// ─── Candidate Profile Detail ───────────────────────────────

export async function fetchCandidateProfile(userId: string): Promise<CandidateProfileDetail> {
  const [profileRes, cpRes] = await Promise.all([
    supabase.from('profiles').select('id, seed_tag, email').eq('id', userId).single(),
    supabase.from('candidate_profiles').select('id, user_id, first_name, last_name, city, photo_url').eq('user_id', userId).single(),
  ]);

  if (!profileRes.data || !cpRes.data) throw new Error('Profil candidat non trouv\u00e9');
  const profile = profileRes.data;
  const cp = cpRes.data;

  const { data: videos } = await supabase
    .from('candidate_videos')
    .select('id, status, duration_seconds, created_at')
    .eq('candidate_id', cp.id)
    .order('created_at', { ascending: false });

  const videoIds = (videos || []).map((v: any) => v.id);
  const interactionCounts: Record<string, { likes: number; views: number }> = {};

  if (videoIds.length > 0) {
    const { data: interactions } = await supabase
      .from('video_interactions')
      .select('video_id, interaction')
      .in('video_id', videoIds)
      .limit(5000);
    (interactions || []).forEach((i: any) => {
      if (!interactionCounts[i.video_id]) interactionCounts[i.video_id] = { likes: 0, views: 0 };
      if (i.interaction === 'like') interactionCounts[i.video_id].likes++;
      else interactionCounts[i.video_id].views++;
    });
  }

  const { data: apps } = await supabase
    .from('applications')
    .select('id, job_offer_id, status, applied_at')
    .eq('candidate_id', cp.id)
    .order('applied_at', { ascending: false });

  let appsList: CandidateProfileDetail['applications'] = [];
  if (apps?.length) {
    const offerIds = apps.map((a: any) => a.job_offer_id);
    const { data: offerData } = await supabase.from('job_offers').select('id, position_name').in('id', offerIds);
    const offerMap = new Map((offerData || []).map((o: any) => [o.id, o.position_name || 'Sans titre']));
    appsList = apps.map((a: any) => ({
      id: a.id,
      offerId: a.job_offer_id,
      offerTitle: offerMap.get(a.job_offer_id) || 'Sans titre',
      status: a.status,
      date: new Date(a.applied_at).toLocaleDateString('fr-FR'),
    }));
  }

  const [{ data: pvData }, { data: favsData }] = await Promise.all([
    supabase.from('profile_views').select('id').eq('candidate_id', cp.id),
    supabase.from('favorites').select('id').eq('target_type', 'candidate').eq('target_id', userId),
  ]);

  const totalLikes = Object.values(interactionCounts).reduce((sum, c) => sum + c.likes, 0);
  const totalViews = Object.values(interactionCounts).reduce((sum, c) => sum + c.views, 0);

  return {
    userId,
    candidateProfileId: cp.id,
    firstName: cp.first_name || '',
    lastName: cp.last_name || '',
    city: cp.city || '',
    email: profile.email || '',
    isSeed: !!profile.seed_tag,
    seedTag: profile.seed_tag || null,
    photoUrl: cp.photo_url || null,
    videos: (videos || []).map((v: any) => ({
      id: v.id,
      status: v.status,
      duration: v.duration_seconds || 0,
      createdAt: new Date(v.created_at).toLocaleDateString('fr-FR'),
      likes: interactionCounts[v.id]?.likes || 0,
      views: interactionCounts[v.id]?.views || 0,
    })),
    stats: {
      totalLikes,
      totalViews,
      profileViewsReceived: (pvData || []).length,
      applicationsCount: (apps || []).length,
      favoritesReceived: (favsData || []).length,
    },
    applications: appsList,
  };
}

// ─── Employer Profile Detail ────────────────────────────────

export async function fetchEmployerProfile(userId: string): Promise<EmployerProfileDetail> {
  const [profileRes, epRes] = await Promise.all([
    supabase.from('profiles').select('id, seed_tag, email').eq('id', userId).single(),
    supabase.from('employer_profiles').select('id, user_id, first_name, last_name, company_name, company_city').eq('user_id', userId).single(),
  ]);

  if (!profileRes.data || !epRes.data) throw new Error('Profil recruteur non trouv\u00e9');
  const profile = profileRes.data;
  const ep = epRes.data;

  const { data: offers } = await supabase
    .from('job_offers')
    .select('id, position_name, establishment_city, contract_type')
    .eq('employer_id', ep.id)
    .order('created_at', { ascending: false });

  const offerIds = (offers || []).map((o: any) => o.id);
  let swipeCounts: Record<string, number> = {};
  let favCounts: Record<string, number> = {};
  let appCounts: Record<string, number> = {};

  if (offerIds.length > 0) {
    const [swRes, favRes, appRes] = await Promise.all([
      supabase.from('swipe_actions').select('target_id').eq('target_type', 'job_offer').in('target_id', offerIds).limit(5000),
      supabase.from('favorites').select('target_id').eq('target_type', 'offer').in('target_id', offerIds).limit(5000),
      supabase.from('applications').select('job_offer_id').in('job_offer_id', offerIds).limit(5000),
    ]);
    swipeCounts = countBy(swRes.data || [], (s: any) => s.target_id);
    favCounts = countBy(favRes.data || [], (f: any) => f.target_id);
    appCounts = countBy(appRes.data || [], (a: any) => a.job_offer_id);
  }

  const [{ data: pvData }, { data: selData }] = await Promise.all([
    supabase.from('profile_views').select('id').eq('employer_id', ep.id),
    supabase.from('employer_selections').select('id').eq('employer_id', ep.id),
  ]);

  const totalApps = Object.values(appCounts).reduce((s, v) => s + v, 0);

  return {
    userId,
    employerProfileId: ep.id,
    firstName: ep.first_name || '',
    lastName: ep.last_name || '',
    companyName: ep.company_name || '',
    city: ep.company_city || '',
    email: profile.email || '',
    isSeed: !!profile.seed_tag,
    seedTag: profile.seed_tag || null,
    offers: (offers || []).map((o: any) => ({
      id: o.id,
      positionName: o.position_name || 'Sans titre',
      city: o.establishment_city || '',
      contractType: o.contract_type || '',
      swipes: swipeCounts[o.id] || 0,
      favorites: favCounts[o.id] || 0,
      applications: appCounts[o.id] || 0,
    })),
    stats: {
      totalOffers: (offers || []).length,
      totalApplicationsReceived: totalApps,
      profileViewsMade: (pvData || []).length,
      selectionsMade: (selData || []).length,
    },
  };
}

// ─── Offer Detail ───────────────────────────────────────────

export async function fetchOfferDetail(offerId: string): Promise<OfferDetailData> {
  const { data: offer, error } = await supabase
    .from('job_offers')
    .select('id, position_name, establishment_city, contract_type, salary_min, salary_max, other_info, seed_tag, employer_id')
    .eq('id', offerId)
    .single();

  if (error || !offer) throw new Error('Offre non trouv\u00e9e');

  let employerName = '';
  let employerUserId = '';
  if (offer.employer_id) {
    const { data: ep } = await supabase.from('employer_profiles').select('user_id, company_name').eq('id', offer.employer_id).maybeSingle();
    if (ep) {
      employerName = ep.company_name || '';
      employerUserId = ep.user_id || '';
    }
  }

  const [swRes, favRes, appRes] = await Promise.all([
    supabase.from('swipe_actions').select('id').eq('target_type', 'job_offer').eq('target_id', offerId),
    supabase.from('favorites').select('id').eq('target_type', 'offer').eq('target_id', offerId),
    supabase.from('applications').select('id, candidate_id, status, applied_at').eq('job_offer_id', offerId).order('applied_at', { ascending: false }),
  ]);

  let appsList: OfferDetailData['applications'] = [];
  if (appRes.data?.length) {
    const cpIds = appRes.data.map((a: any) => a.candidate_id);
    const { data: cpData } = await supabase.from('candidate_profiles').select('id, first_name, last_name').in('id', cpIds);
    const cpMap = new Map((cpData || []).map((c: any) => [c.id, `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Inconnu']));
    appsList = appRes.data.map((a: any) => ({
      id: a.id,
      candidateProfileId: a.candidate_id,
      candidateName: cpMap.get(a.candidate_id) || 'Inconnu',
      status: a.status,
      date: new Date(a.applied_at).toLocaleDateString('fr-FR'),
    }));
  }

  return {
    id: offer.id,
    positionName: offer.position_name || 'Sans titre',
    city: offer.establishment_city || '',
    contractType: offer.contract_type || '',
    salaryMin: offer.salary_min,
    salaryMax: offer.salary_max,
    description: offer.other_info || '',
    employerName,
    employerUserId,
    isSeed: !!offer.seed_tag,
    seedTag: offer.seed_tag || null,
    stats: {
      swipes: (swRes.data || []).length,
      favorites: (favRes.data || []).length,
      applications: (appRes.data || []).length,
    },
    applications: appsList,
  };
}

// ─── Targeted Boost Wrappers ────────────────────────────────

export async function boostVideo(params: {
  campaignTag: string;
  videoId: string;
  interaction: 'like' | 'view';
  count: number;
  spreadDays?: number;
}): Promise<number> {
  const seedProfiles = await getSeedCandidates(params.campaignTag);
  const days = params.spreadDays ?? 3;

  const rows: any[] = [];
  for (let i = 0; i < params.count; i++) {
    rows.push({
      user_id: seedProfiles[i % seedProfiles.length],
      video_id: params.videoId,
      video_source: 'candidate',
      interaction: params.interaction,
      seed_tag: params.campaignTag,
      created_at: spreadTimestamp(days),
    });
  }

  const uniqueRows = dedup(rows, (r) => `${r.user_id}-${r.video_id}-${r.interaction}`);
  if (uniqueRows.length === 0) return 0;

  const { error } = await supabase.from('video_interactions').upsert(uniqueRows, {
    onConflict: 'user_id,video_id,interaction',
    ignoreDuplicates: true,
  });
  if (error) throw error;

  await logAudit('boost_video', 'video', params.videoId, {
    type: params.interaction,
    count: uniqueRows.length,
    campaign: params.campaignTag,
  });
  return uniqueRows.length;
}

export async function boostCandidateProfileViews(params: {
  campaignTag: string;
  candidateProfileId: string;
  count: number;
  spreadDays?: number;
}): Promise<number> {
  return generateEmployerProfileViews({
    campaignTag: params.campaignTag,
    targetCandidateIds: [params.candidateProfileId],
    count: params.count,
    spreadDays: params.spreadDays,
  });
}

export async function boostOfferApplications(params: {
  campaignTag: string;
  offerId: string;
  count: number;
  spreadDays?: number;
}): Promise<number> {
  return generateSeedApplications({
    campaignTag: params.campaignTag,
    targetOfferIds: [params.offerId],
    count: params.count,
    spreadDays: params.spreadDays,
  });
}
